import { BetslipApplicationState } from "../../../../store/state/BetslipApplicationState";
import {
  emitAcceptOddsMovementPreferenceToggled,
  emitBetslipDepositToPlaceRequested,
} from "../../../../contract/betslip-api";
import {
  type BetslipSportsbookAcceptOddsMovementToggleAction,
  UI__BETSLIP_SBK_ACCEPT_ODDS_MOVEMENT_TOGGLE,
} from "../../../../store/state/preferences/preferences-actions";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
  getSportsbookBettingState,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../../store/state/sportsbook-betting/betting-resolver";
import {
  getAllUniqueRunnersFailures,
  getSportsbookBettingImplyRunnerFailures,
  getSportsbookBettingValidations,
  getSportsbookBettingRunners,
} from "../../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";

import {
  BetslipSportsbookPlaceBetsClick,
  BetslipSportsbookIncrementStakeAction,
  UI__BETSLIP_SBK_DEPOSIT_TO_PLACE_BET_CLICK,
  UI__BETSLIP_SBK_PLACE_BETS_CLICK,
  UI__BETSLIP_SBK_INCREMENT_STAKE_ACTION,
} from "@ppb/tbd-store/actions/betslip";
import {
  BetslipSportsbookDepositToPlaceBetClick,
  BetslipSportsbookStakeInputChange,
  UI__BETSLIP_SBK_STAKE_INPUT_CHANGE,
} from "../../../../store/state/actions";
import {
  BETTING__DEPOSIT_TO_PLACE_BET,
  BETTING__SBK_PLACE_BETS,
  BETTING__SBK_UPDATE_COMBINATION_STAKE_ACTION,
  BETTING__SBK_INCREMENT_STAKE_ACTION,
  BettingDepositToPlaceBetAction,
  BettingSportsbookPlaceBetsAction,
  BettingSportsbookUpdateCombinationStakeAction,
  BettingSportsbookIncrementStakeAction,
} from "@ppb/tbd-store/actions/betting";
import { FallbackIconType, QuickStakesProps, SwitchOnChange } from "@ppb/the-wall-common/types";
import type { Dispatch } from "redux";
import { buildSbkBalanceAfterBetLabel } from "../../../../helpers/betslip-balance-helper";
import { i18n } from "../../../../helpers/i18n";
import {
  buildQuickStakesSelector,
  createGetSbkIsDepositRequiredSelector,
  createGetSbkRequiredDepositValueSelector,
} from "../../../betslip-mapper";
import {
  buildSportsbookTransactionalError,
  createAreAllCombinationsClosedOrSuspendedSelector,
  hasSGMFailures,
  buildSelection,
} from "../../../connected-sportsbook-betslip-mapper";
import { hasAnyMarketClosedFailure, hasAnyMarketSuspendedFailure } from "@ppb/tbd-store/helpers/sportsbook-betting";
import { isStakeValid } from "../../../../helpers/territory-rules";
import { buildSbkPlaceBetButtonLabels } from "../../../../helpers/betslip-place-button-helper";
import { currencyFormatWithDecimalPlaces } from "../../../../formatters/currency-adapter";
import { buildPotentialReturns } from "../../../betslip-formatters";
import { BettingState, RUNNER_FAILURE_CODES, VALIDATION_TYPES } from "@ppb/betslip-core";
import { createGetBetslipHeaderViewModel, getQuickBetslipSelectionMetadata } from "./vm-builder";
import type {
  BetslipLocalization,
  SportsbookBetslipBalance,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
} from "../../../../contract";
import {
  getLastSuccessfulStake,
  getBetslipPlaceStatus,
  getHasUserChangedOddsMovementPreference,
  getBetslipGroup,
  getIsBetslipLoaded,
  getSportsbookOddsMovement,
} from "../../../../store/state/betslip-module/betslip-module-selectors";
import { buildBetOptions, buildEachWaySubtitle } from "../../../BetControls/bet-controls-mapper";
import {
  dispatchEachWayPress as dispatchEachWayToggle,
  dispatchStartingPricePress,
} from "../../../BetControls/map-to-props-factory";
import { GenerosityWalletType } from "../../../../helpers/generosity-wallets";
import {
  createGenerosityPropsSelector,
  dispatchGenerosityPress as dispatchGenerosityToggle,
  dispatchGenerosityRemoveAction as dispatchGenerosityRemove,
  dispatchGenerosityWalletRemoveAction as dispatchGenerosityWalletRemove,
  type GenerosityStateProps,
} from "../../../generosity-mapper";
import { createOddsMovementSelector } from "../../../../store/state/betslip-module/betslip-odds-movement-selectors";

type QuickBetslipHeader = {
  counter: number;
  title: string;
  selectionDetails: string;
  moreLabel: string | null;
  isPriceBoostMultiple: boolean;
};

export type CardProps = {
  header: QuickBetslipHeader;
  combinationIds: string[];
  isStakeValid: boolean;
  isLastStakeValid: boolean;
  placeButtonLabel: string;
  placeBtnLoadingLabel?: string;
  termsUrl: string | null;
  hasFailures?: boolean;
  balanceAfterBet: string;
  totalStake: number;
  totalReturns: string;
  stake?: number;
  currencySymbol: string;
  isPlaceDisabled: boolean;
  isPlacing: boolean;
  hasPlaceError: boolean;
  hasCTALoading: boolean;
  isDepositRequired: boolean;
  requiredDepositValue: number;
  lastSuccessfulStake?: number;
  isOddsMovementOn: boolean;
  showAcceptOddsMovementAlert: boolean;
  placeBtnSecondaryLabel?: string | string[];
  reverseLabels?: boolean;
  icon?: string;
  silkFallbackType?: FallbackIconType;
  hasEachWay: boolean;
  hasStartingPrice: boolean;
  isEachWaySelected: boolean;
  isStartingPriceSelected: boolean;
  eachWaySubtitle?: string;
  multiplier?: string;
  areBetOptionsDisabled: boolean;
  oddsMovementLabels: { message: string; detailMessage: string };
  i18n: {
    termsLabel: string;
    termsLinkLabel: string;
    stakePlaceholder: string;
    totalStakeLabel: string;
    totalReturnsLabel: string;
    generosityAlertRemoveLabel: string;
    eachWayTitle: string;
    startingPriceTitle: string;
  };
  quickStakes: QuickStakesProps["quickStakes"];
} & GenerosityStateProps;
export type StateProps = CardProps | Record<string, never>;

export type ContainerProps = {
  combinationId: string;
  onClick: () => void;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
  preferences: SportsbookBetslipPreferences;
  balance: SportsbookBetslipBalance;
};

const i18nLabels = {
  termsLabel: i18n({ key: "I18N.BETSLIP.BETS_ACCEPTED_IN_ACCORDANCE_WITH" }),
  termsLinkLabel: i18n({ key: "I18N.BETSLIP.TEXT_RULES" }),
  stakePlaceholder: i18n({ key: "I18N.BETSLIP.STAKE_PLACEHOLDER" }),
  totalStakeLabel: i18n({ key: "I18N.BETSLIP.BALANCE_AFTER_BET" }),
  totalReturnsLabel: i18n({ key: "I18N.BETSLIP.POTENTIAL_RETURNS" }),
  generosityAlertRemoveLabel: i18n({ key: "I18N.BETSLIP.REMOVE" }),
  eachWayTitle: i18n({ key: "I18N.QUICK_BETSLIP.TITLE.EW" }),
  startingPriceTitle: i18n({ key: "I18N.BETSLIP.TITLE.STARTING_PRICE" }),
};

const hasLegsWithMarketFailures = (
  combination: BettingState.Combination,
  bettingLegs: BettingState.LegsMap,
  runnerFailures: BettingState.ImplyRunnerFailuresMap,
) =>
  combination.legs.some((leg) => {
    const bettingLeg = bettingLegs[leg];
    return bettingLeg.runners.some(
      (runner) =>
        runnerFailures[runner] &&
        (hasAnyMarketClosedFailure(runnerFailures[runner]) || hasAnyMarketSuspendedFailure(runnerFailures[runner])),
    );
  });

function isLastSuccessfulStakeValid(
  lastSuccessfulStake: number | undefined,
  combination: BettingState.Combination,
): lastSuccessfulStake is number {
  if (!lastSuccessfulStake || !combination.minStake || combination.calculatedMaxStake === Number.MAX_VALUE) {
    return false;
  }

  return lastSuccessfulStake >= combination.minStake && lastSuccessfulStake <= combination.calculatedMaxStake;
}

const ACCEPT_ODDS_MOVEMENT_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_DESCRIPTION" }),
};

const ODDS_MOVEMENT_ON_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_ON" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_ON_DESCRIPTION" }),
};

const ODDS_MOVEMENT_OFF_LABELS = {
  message: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_OFF" }),
  detailMessage: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_OFF_DESCRIPTION" }),
};

const acceptOddsMovementLabels = (oddsMovementEnabled: boolean, hasUserChangedOddsMovement: boolean) => {
  if (!hasUserChangedOddsMovement && !oddsMovementEnabled) {
    return ACCEPT_ODDS_MOVEMENT_LABELS;
  }

  if (oddsMovementEnabled) {
    return ODDS_MOVEMENT_ON_LABELS;
  }

  return ODDS_MOVEMENT_OFF_LABELS;
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getIsDepositRequired = createGetSbkIsDepositRequiredSelector();
  const getSbkRequiredDepositValueSelector = createGetSbkRequiredDepositValueSelector();
  const getAreAllCombinationsClosedOrSuspended = createAreAllCombinationsClosedOrSuspendedSelector();
  const getBetslipHeaderViewModel = createGetBetslipHeaderViewModel();
  const getGenerosityProps = createGenerosityPropsSelector();
  const getOddsMovementState = createOddsMovementSelector();
  const getQuickStakes = buildQuickStakesSelector();

  return (
    state: BetslipApplicationState,
    { combinationId, localization, config, capabilities, preferences, balance }: ContainerProps,
  ): StateProps => {
    const { currencySymbol } = localization;
    if (!getIsBetslipLoaded(state)) {
      return {};
    }

    const combinationsState = getSportsbookBettingCombinations(state);
    const combination = combinationsState[combinationId];
    if (!combination) {
      return {};
    }
    const legs = getSportsbookBettingLegs(state);
    const runners = getSportsbookBettingRunners(state);
    const resolvers = getBettingResolvers(getBetslipGroup(state));
    const bettingRunnersMetadata = resolvers.getMetadata(state);
    const metadata = getQuickBetslipSelectionMetadata(combination, bettingRunnersMetadata, legs);

    if (!metadata) {
      return {};
    }

    const { icon, silkFallbackType } = buildSelection(metadata, localization);

    const validations = getSportsbookBettingValidations(state);
    const runnerFailures = getSportsbookBettingImplyRunnerFailures(state);
    const isDepositRequired = getIsDepositRequired(state, balance);
    const requiredDepositValue = getSbkRequiredDepositValueSelector(state, balance) ?? 0;
    const areAllClosedOrSuspended = getAreAllCombinationsClosedOrSuspended(state);
    const transactionalError = buildSportsbookTransactionalError(state);

    const combinationsValues = Object.values(combinationsState);
    const combinationValidations = validations.combinations[combinationId] ?? [];
    const hasValidStakedCombinations = combinationsValues.some((combination) => {
      const validCombination = !!combination.totalCombinedStake;
      return validCombination && !hasLegsWithMarketFailures(combination, legs, runnerFailures);
    });
    const isPlacing = getBetslipPlaceStatus(state) === "INPROGRESS";
    const sportsbookOddsMovement = getSportsbookOddsMovement(state);
    const oddsMovementMap = getOddsMovementState(sportsbookOddsMovement);
    const oddsWithMovement = Object.values(oddsMovementMap).filter((each) => each.movement !== null);
    const hasAnyOddChanged = oddsWithMovement.length >= 1;

    const hasFailures = Object.keys(runnerFailures).some((key) => hasSGMFailures(runnerFailures[key]));
    const { totalStake, isBonusSelected, totalPotentialReturns, totalCombinedStake } = getSportsbookBettingState(state);
    const accountBalance = balance.available ?? 0;
    const areBetOptionsDisabled = isPlacing || hasLegsWithMarketFailures(combination, legs, runnerFailures);
    const betOptions = buildBetOptions(combination, legs, runners);

    const generosityProps = getGenerosityProps(state, {
      combination,
      combinationId,
      // Unlike the full betslip, quick betslip has no footer for single-bet Free Bets alerts.
      isSingleBetBetslip: false,
      localization,
      oddsDisplayPreference: preferences.oddsDisplay,
    });

    const hasBlockingValidations = combinationValidations.some(
      (validation) => validation.type !== VALIDATION_TYPES.BONUS_USAGE_ACCA_INSURANCE_INCOMPATIBLE,
    );

    const isPlaceDisabled = !hasValidStakedCombinations || hasBlockingValidations;
    const hasCTALoading = !isDepositRequired;

    const isAcceptOddsMovementAlertEnabled = capabilities.acceptOddsMovementAlert.enabled;

    const balanceAfterBet = buildSbkBalanceAfterBetLabel(localization, {
      accountBalance,
      totalStake: totalStake ?? 0,
      isOldUseBonusActive: isBonusSelected,
      isLoggedIn: true,
    });
    const quickStakes = getQuickStakes(preferences.quickStakes, localization);

    const termsUrl = config.termsUrls.general ?? null;

    const formattedTotalStake = currencyFormatWithDecimalPlaces({
      ...localization,
      value: totalStake,
      decimalPlaces: 2,
    });

    const totalReturns = buildPotentialReturns(totalCombinedStake, totalPotentialReturns, localization);

    const { label, secondaryLabel, loadingLabel, reverseLabels } = buildSbkPlaceBetButtonLabels({
      isPlacing,
      hasOddsChanged: hasAnyOddChanged,
      isDepositRequired,
      isLoggedIn: true,
      hasStake: !!totalStake,
      isSuspended: areAllClosedOrSuspended,
      interpolatedValues: {
        stake: formattedTotalStake,
      },
    });

    const lastSuccessfulStake = getLastSuccessfulStake(state);
    // Balance or stake limits can land after the first render, delaying the pre-fill.
    // Preview the last stake in the title meanwhile so it does not flicker when the pre-fill lands.
    const isLastStakePending =
      !!lastSuccessfulStake && !combination.stake && (balance.available === null || !combination.minStake);

    const header = getBetslipHeaderViewModel(state, {
      combinationId,
      localization,
      oddsDisplay: preferences.oddsDisplay,
      fallbackStake: isLastStakePending ? lastSuccessfulStake : undefined,
    });

    const oddsMovement = preferences.acceptOddsMovement;
    const hasUserChangedOddsMovement = getHasUserChangedOddsMovementPreference(state);
    const placeRunnerFailures = getAllUniqueRunnersFailures(state);
    const showAcceptOddsMovementAlert =
      placeRunnerFailures.length > 0 &&
      placeRunnerFailures.some((failure) => failure === RUNNER_FAILURE_CODES.REQUESTED_PRICE_NOT_AVAILABLE);
    const oddsMovementLabels = acceptOddsMovementLabels(oddsMovement, hasUserChangedOddsMovement);

    return {
      combinationIds: [combinationId],
      isStakeValid: isStakeValid(localization, combinationValidations),
      header,
      termsUrl,
      hasFailures,
      balanceAfterBet,
      ...generosityProps,
      totalStake,
      stake: combination.stake ? combination.stake : undefined,
      totalReturns: totalReturns,
      currencySymbol,
      icon,
      silkFallbackType,
      ...betOptions,
      areBetOptionsDisabled,
      eachWaySubtitle: buildEachWaySubtitle(combination),
      isLastStakeValid:
        isLastSuccessfulStakeValid(lastSuccessfulStake, combination) &&
        // FIXME: Ideally this would use a @ppb/betslip-core calculator for the stake value to compare.
        // This is very basic and brittle and contemplates just straight stakes but it is enough for the quick betslip for an experiment.
        // Worst case scenario, this just does not pre-populate the input
        !getIsDepositRequired(state, balance, lastSuccessfulStake * combination.numLines),
      lastSuccessfulStake,
      quickStakes,
      isOddsMovementOn: oddsMovement,
      showAcceptOddsMovementAlert: isAcceptOddsMovementAlertEnabled ? showAcceptOddsMovementAlert : false,
      placeBtnSecondaryLabel: secondaryLabel,
      reverseLabels,
      oddsMovementLabels,
      i18n: i18nLabels,
      isPlaceDisabled,
      isPlacing,
      hasPlaceError: !!transactionalError,
      isDepositRequired,
      requiredDepositValue,
      placeButtonLabel: label,
      placeBtnLoadingLabel: loadingLabel,
      hasCTALoading,
    };
  };
};

export type DispatchProps = {
  dispatchStakeChange: ({ id, newValue }: { id: string; newValue?: number }) => void;
  dispatchIncrementPress: (combinationId: string, quickStakeValue: number, currencySymbol?: string) => void;
  dispatchPlacement: () => void;
  dispatchDepositRedirect: (amount?: number) => void;
  dispatchOddsMovementChange: SwitchOnChange;
  dispatchEachWayPress: (combinationId: string, isSelected: boolean) => void;
  dispatchStartPricePress: (combinationId: string, isSelected: boolean) => void;
  dispatchGenerosityPress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchGenerosityWalletRemoveAction: (combinationIds: string[], type?: GenerosityWalletType) => void;
  dispatchGenerosityRemoveAction: () => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchEachWayPress: (combinationId, isEachWaySelected) => {
    dispatchEachWayToggle(dispatch, [combinationId], isEachWaySelected);
  },
  dispatchStartPricePress: (combinationId, isStartingPriceSelected) => {
    dispatchStartingPricePress(dispatch, [combinationId], isStartingPriceSelected);
  },
  dispatchStakeChange: ({ id, newValue }): void => {
    dispatch<BetslipSportsbookStakeInputChange>({
      type: UI__BETSLIP_SBK_STAKE_INPUT_CHANGE,
      payload: { combinationId: id, stake: newValue },
    });
    dispatch<BettingSportsbookUpdateCombinationStakeAction>({
      type: BETTING__SBK_UPDATE_COMBINATION_STAKE_ACTION,
      payload: { combinationId: id, stake: newValue },
    });
  },
  dispatchPlacement: () => {
    dispatch<BetslipSportsbookPlaceBetsClick>({
      type: UI__BETSLIP_SBK_PLACE_BETS_CLICK,
    });
    dispatch<BettingSportsbookPlaceBetsAction>({
      type: BETTING__SBK_PLACE_BETS,
    });
  },
  dispatchDepositRedirect: (amount?: number) => {
    dispatch<BetslipSportsbookDepositToPlaceBetClick>({ type: UI__BETSLIP_SBK_DEPOSIT_TO_PLACE_BET_CLICK });
    dispatch<BettingDepositToPlaceBetAction>({ type: BETTING__DEPOSIT_TO_PLACE_BET });
    emitBetslipDepositToPlaceRequested({ amount });
  },
  dispatchIncrementPress: (combinationId, quickStakeValue, currencySymbol) => {
    dispatch<BettingSportsbookIncrementStakeAction>({
      type: BETTING__SBK_INCREMENT_STAKE_ACTION,
      payload: { combinationId, increment: quickStakeValue },
    });
    dispatch<BetslipSportsbookIncrementStakeAction>({
      type: UI__BETSLIP_SBK_INCREMENT_STAKE_ACTION,
      payload: { increment: quickStakeValue, currencySymbol },
    });
  },
  dispatchOddsMovementChange: (isChecked: boolean) => {
    dispatch<BetslipSportsbookAcceptOddsMovementToggleAction>({
      type: UI__BETSLIP_SBK_ACCEPT_ODDS_MOVEMENT_TOGGLE,
      payload: { enabled: isChecked },
    });
    emitAcceptOddsMovementPreferenceToggled({ enabled: isChecked });
  },
  dispatchGenerosityPress: (combinationIds, isSelected) =>
    dispatchGenerosityToggle(dispatch, combinationIds, isSelected),
  dispatchGenerosityWalletRemoveAction: (combinationIds: string[], type?: GenerosityWalletType) =>
    dispatchGenerosityWalletRemove(dispatch, combinationIds, type),
  dispatchGenerosityRemoveAction: () => dispatchGenerosityRemove(dispatch),
});
